import { Container, Graphics, Rectangle, Renderer, Sprite, Texture } from "pixi.js"
import { NumberedTileMeta, TileSize } from "game-common/models"
import { contentTextureGeneration } from "../content_textures"
import { compositeArtPixels } from "../sprites/composite_art_pixels"

export interface RootEdge { x: number; y: number; bark?: boolean }
export interface RootBlade extends RootEdge { height: number; color: number }

/** Bottom-most opaque pixel of each root column, excluding the canopy above the last tile row. */
export const treeRootContour = (rgba: ArrayLike<number>, width: number, height: number): RootEdge[] => {
    const edges: RootEdge[] = []
    for (let x = 0; x < width; x++) for (let y = height - 1; y >= Math.max(0, height - TileSize); y--) {
        if (rgba[(y * width + x) * 4 + 3] > 128) { edges.push({ x, y }); break }
    }
    return edges
}

/** The root columns standing on the ground: walking out from the lowest root, the outline may climb a few
 * pixels a column (a spruce's skirt, a flared trunk), but a sudden climb is a canopy hanging clear of the
 * ground (a short oak's), and nothing past it touches the ground. */
export const groundedRoots = (edges: RootEdge[], maxClimb: number = 3): RootEdge[] => {
    if (!edges.length) return edges
    const lowest = edges.reduce((best, edge, i) => edge.y > edges[best].y ? i : best, 0)
    const joined = (from: RootEdge, to: RootEdge) => Math.abs(to.x - from.x) === 1 && from.y - to.y <= maxClimb
    let left = lowest, right = lowest
    while (left > 0 && joined(edges[left], edges[left - 1])) left--
    while (right < edges.length - 1 && joined(edges[right], edges[right + 1])) right++
    return edges.slice(left, right + 1)
}

/** Marks the root columns that are opaque bark just above the outline: a trunk's foot, as opposed to a
 * spruce's skirt of needles or an old tree's baked, semi-transparent shadow. */
export const markBarkRoots = (rgba: ArrayLike<number>, width: number, edges: RootEdge[]): RootEdge[] =>
    edges.map(edge => {
        const i = (Math.max(0, edge.y - 1) * width + edge.x) * 4
        const bark = rgba[i + 3] > 250 && rgba[i] > rgba[i + 2] + 4 && rgba[i] >= rgba[i + 1]
        return bark ? { ...edge, bark } : edge
    })

export const rootNoise = (x: number, y: number, seed: number) => {
    let h = Math.imul(x + seed, 374761393) ^ Math.imul(y, 668265263)
    h = Math.imul(h ^ h >>> 13, 1274126177)
    return ((h ^ h >>> 16) >>> 0) / 4294967295
}

// One shared palette and density for all grass surfaces.
const PALETTE = [0x3e872a, 0x4d932a, 0x6a9e31]
export const rootFringeBlades = (edges: RootEdge[]): RootBlade[] => groundedRoots(edges).flatMap(edge => {
    if (edge.x % 2 || rootNoise(edge.x, edge.y, 0) > 0.58) return []
    const variation = rootNoise(edge.x, edge.y, 71)
    return [{ x: edge.x, y: edge.y + 1, height: 2 + Math.floor(variation * 4),
        color: PALETTE[Math.floor(variation * 3)] }]
})

/** A single ground sample at the lowest root, in the composite's local coordinates. */
export const rootGroundPoint = (edges: RootEdge[]): RootEdge => {
    const bottom = Math.max(...edges.map(edge => edge.y))
    const roots = edges.filter(edge => edge.y >= bottom - 2)
    return { x: (Math.min(...roots.map(edge => edge.x)) + Math.max(...roots.map(edge => edge.x))) / 2,
        y: bottom + 2 }
}

const contours = new Map<string, RootEdge[]>()
let contourGeneration = -1

/** Read original atlas pixels once per tree variant, never GPU-read back a generated texture. */
export const treeRootEdges = (tilesetId: string, compositeId: string, columns: number,
    children: NumberedTileMeta[]): RootEdge[] | undefined => {
    const generation = contentTextureGeneration()
    if (generation !== contourGeneration) { contours.clear(); contourGeneration = generation }
    const key = `${tilesetId}:${compositeId}`
    if (contours.has(key)) return contours.get(key)
    const art = compositeArtPixels(tilesetId, columns, children)
    if (!art) return undefined
    const edges = markBarkRoots(art.data, art.width, treeRootContour(art.data, art.width, art.height))
    contours.set(key, edges)
    return edges
}

// Share the baked texture between every tree of the same variant. No extra display objects
// or terrain polling survive into the frame loop; a map redraw makes the grass decision again.
type TextureBaker = Pick<Renderer, "generateTexture">
const baked = new WeakMap<Texture, { renderer: TextureBaker; texture: Texture }>()
export const treeRootFringeTexture = (tree: Texture, edges: RootEdge[], renderer: TextureBaker): Texture => {
    const cached = baked.get(tree)
    if (cached?.renderer === renderer && !cached.texture.baseTexture.destroyed) return cached.texture
    const surface = new Container()
    surface.addChild(new Sprite(tree))
    const fringe = new Graphics()
    surface.addChild(fringe)
    for (const blade of rootFringeBlades(edges)) {
        fringe.beginFill(blade.color)
        fringe.drawRect(blade.x, blade.y - blade.height + 1, 1, blade.height)
        fringe.drawRect(blade.x + (blade.x % 4 ? -1 : 1), blade.y - blade.height, 1, 1)
        fringe.endFill()
    }
    const texture = renderer.generateTexture(surface, { region: new Rectangle(0, 0, tree.width, tree.height) })
    surface.destroy({ children: true })
    baked.set(tree, { renderer, texture })
    return texture
}
